import * as htmlparser from 'htmlparser2';
import { unescape } from 'lodash';
import { inflateRaw, deflateRaw } from 'pako';

/**
 * checks if passed value is a plain js object
 */ const isPlainObject = (value)=>{
    if (!(value !== null && typeof value === "object")) return false;
    const proto = Object.getPrototypeOf(value);
    if (proto == null) return true;
    const protoConstructor = Object.hasOwnProperty.call(proto, "constructor") && proto.constructor;
    return typeof protoConstructor === "function" && protoConstructor.toString() === Object.toString();
}; // https://stackoverflow.com/a/37865170

/**
 * maps object keys to snake_case
 */ const mapKeysToSnakeCase = (data)=>{
    const mapFn = (s)=>{
        s.match(/[A-Z]/g)?.forEach((m)=>s = s.replace(m, "_" + m.toLowerCase()));
        return s;
    };
    return deepMapKeys(data, mapFn);
};
/**
 * maps object keys to camelCase
 */ const mapKeysToCamelCase = (data)=>{
    const mapFn = (s)=>{
        s.match(/_\w/g)?.forEach((m)=>s = s.replace(m, m.replace(/_/g, "").toUpperCase()));
        return s;
    };
    return deepMapKeys(data, mapFn);
};
/**
 * Maps object key names based on passed function
 */ const deepMapKeys = (obj, mapFn)=>{
    return Array.isArray(obj) ? obj.map((val)=>deepMapKeys(val, mapFn)) : isPlainObject(obj) ? Object.keys(obj).reduce((acc, current)=>{
        const key = mapFn(current);
        const val = obj[current];
        acc[key] = val !== null && typeof val === "object" ? deepMapKeys(val, mapFn) : val;
        return acc;
    }, {}) : obj;
};

var APIMethod = /*#__PURE__*/ function(APIMethod) {
    APIMethod["GET"] = "GET";
    APIMethod["POST"] = "POST";
    APIMethod["PUT"] = "PUT";
    APIMethod["DELETE"] = "DELETE";
    APIMethod["PATCH"] = "PATCH";
    return APIMethod;
}({});

const parseCompanySettings = (data)=>{
    try {
        if (typeof data === "string") return JSON.parse(data);
        return data;
    } catch (err) {
        return data;
    }
};

/**
 * Parses JS object data to URLSearchParams
 */ const parseFormData = (data, removeEmpty = false, mapRequestKeysToSnakeCase = true)=>{
    const parsed = mapRequestKeysToSnakeCase ? mapKeysToSnakeCase(data) : data;
    const body = new URLSearchParams();
    Object.keys(parsed).forEach((k)=>{
        if (!removeEmpty || ![
            null,
            undefined
        ].includes(parsed[k])) body.append(k, parsed[k]);
    });
    return body;
};

const getContrastingFontColor = (color)=>{
    const hex = (color || "#4098F4").replace(/^#/, "");
    const normalized = hex.length === 3 ? hex.split("").map((channel)=>`${channel}${channel}`).join("") : hex.length === 8 ? hex.slice(0, 6) : hex;
    if (!/^[\da-f]{6}$/i.test(normalized)) return "#ffffff";
    const channels = [
        0,
        2,
        4
    ].map((index)=>parseInt(normalized.slice(index, index + 2), 16) / 255);
    const luminance = channels.map((channel)=>channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4).reduce((total, channel, index)=>total + channel * [
            0.2126,
            0.7152,
            0.0722
        ][index], 0);
    return luminance > 0.179 ? "#000000" : "#ffffff";
};

const cleanObject = (object, cleanEmptyValues)=>{
    Object.keys(object).forEach((key)=>{
        if (!object[key] && (cleanEmptyValues || ![
            "boolean",
            "number",
            "string"
        ].includes(typeof object[key]))) delete object[key];
    });
    return {
        ...object
    };
};

const cleanHtmlContent = (htmlText)=>{
    let text = "";
    const parser = new htmlparser.Parser({
        onclosetag: (tagName)=>{
            if (tagName === "p") text += "\n";
            else text += " ";
        },
        ontext: (c)=>{
            text += c;
        }
    }, {
        decodeEntities: true
    });
    parser.write(htmlText);
    parser.end();
    return text.replace(/ +/g, " ");
};

const parsePrice = (price, americanFormat = false, forcedDecimals = false)=>{
    if (!price || price === "") return "0";
    const parsed = forcedDecimals ? Number(price).toFixed(2) : price.toString();
    const s = parsed.split(parsed.includes(",") ? "," : ".") || "";
    const p = [];
    s[0].split("").reverse().forEach((x, i)=>{
        p.push(x);
        if (i % 3 === 2 && i < s[0].length - 1) p.push(americanFormat ? "," : " ");
    });
    return `${p.reverse().join("")}${s[1] ? "." + s[1] : ""}`;
};

var MinAvgMaxKeys = /*#__PURE__*/ function(MinAvgMaxKeys) {
    MinAvgMaxKeys["MIN"] = "min";
    MinAvgMaxKeys["AVG"] = "avg";
    MinAvgMaxKeys["MAX"] = "max";
    return MinAvgMaxKeys;
}({});
var CommentTypes = /*#__PURE__*/ function(CommentTypes) {
    CommentTypes["INTERNAL"] = "internal";
    CommentTypes["EXTERNAL"] = "external";
    return CommentTypes;
}({});
var Tables = /*#__PURE__*/ function(Tables) {
    Tables["SUMMARY"] = "summary";
    Tables["TIMELINE"] = "timeline";
    Tables["BREAKDOWN"] = "breakdown";
    Tables["DESCRIPTION"] = "description";
    return Tables;
}({});
var EstimationUnits = /*#__PURE__*/ function(EstimationUnits) {
    EstimationUnits["HOURS"] = "hours";
    EstimationUnits["DAYS"] = "days";
    return EstimationUnits;
}({});
var PriceModifiers = /*#__PURE__*/ function(PriceModifiers) {
    PriceModifiers["DISCOUNT"] = "discount";
    PriceModifiers["OVERHEAD"] = "overhead";
    return PriceModifiers;
}({});
var PriceModifierModes = /*#__PURE__*/ function(PriceModifierModes) {
    PriceModifierModes["FIXED_PRICE"] = "fixedPrice";
    PriceModifierModes["PERCENT"] = "percent";
    return PriceModifierModes;
}({});
var TaskTypes = /*#__PURE__*/ function(TaskTypes) {
    TaskTypes["WORKLOAD"] = "workload";
    TaskTypes["MATERIAL"] = "material";
    TaskTypes["FIXED_PRICE"] = "fixedPrice";
    return TaskTypes;
}({});
var Totals = /*#__PURE__*/ function(Totals) {
    Totals["COST"] = "cost";
    Totals["HOURS"] = "hours";
    Totals["TIME"] = "time";
    return Totals;
}({});
var BreakdownColumns = /*#__PURE__*/ function(BreakdownColumns) {
    BreakdownColumns["NUMERATION"] = "numeration";
    BreakdownColumns["TITLE"] = "title";
    BreakdownColumns["LABELS"] = "labels";
    BreakdownColumns["DESCRIPTION"] = "description";
    BreakdownColumns["ESTIMATES"] = "estimates";
    BreakdownColumns["PRICE"] = "price";
    return BreakdownColumns;
}({});
var SummaryColumns = /*#__PURE__*/ function(SummaryColumns) {
    SummaryColumns["TITLE"] = "title";
    SummaryColumns["TEAM"] = "team_members";
    SummaryColumns["TIME"] = "time";
    SummaryColumns["HOURS"] = "hours";
    SummaryColumns["RATE"] = "rate";
    SummaryColumns["COST"] = "cost";
    SummaryColumns["C_COST"] = "company_cost";
    SummaryColumns["C_UNIT_COST_INNER"] = "company_inner_unit_cost";
    SummaryColumns["C_UNIT_COST"] = "company_unit_cost";
    SummaryColumns["C_PROFIT"] = "company_profit";
    SummaryColumns["C_PROFITABILITY"] = "company_profitability";
    return SummaryColumns;
}({});
var DescriptionModes = /*#__PURE__*/ function(DescriptionModes) {
    DescriptionModes[DescriptionModes["TOOLTIP"] = 1] = "TOOLTIP";
    DescriptionModes[DescriptionModes["ROW"] = 2] = "ROW";
    DescriptionModes[DescriptionModes["COLUMN"] = 3] = "COLUMN";
    return DescriptionModes;
}({});
var RoundTypes = /*#__PURE__*/ function(RoundTypes) {
    RoundTypes[RoundTypes["SOFT"] = 1] = "SOFT";
    RoundTypes[RoundTypes["HARD"] = 2] = "HARD";
    return RoundTypes;
}({});
var Rounding = /*#__PURE__*/ function(Rounding) {
    Rounding[Rounding["DECIMALS"] = 0] = "DECIMALS";
    Rounding[Rounding["FLOOR"] = 1] = "FLOOR";
    Rounding[Rounding["CEIL"] = 2] = "CEIL";
    Rounding[Rounding["FULL"] = 3] = "FULL";
    return Rounding;
}({});
var RoundIntervals = /*#__PURE__*/ function(RoundIntervals) {
    RoundIntervals[RoundIntervals["QUARTER"] = 4] = "QUARTER";
    RoundIntervals[RoundIntervals["HALF"] = 2] = "HALF";
    RoundIntervals[RoundIntervals["FULL"] = 1] = "FULL";
    return RoundIntervals;
}({});
var PriceFormats = /*#__PURE__*/ function(PriceFormats) {
    PriceFormats[PriceFormats["EUROPEAN"] = 1] = "EUROPEAN";
    PriceFormats[PriceFormats["AMERICAN"] = 2] = "AMERICAN";
    return PriceFormats;
}({});
var SectionFormats = /*#__PURE__*/ function(SectionFormats) {
    SectionFormats[SectionFormats["ROW"] = 1] = "ROW";
    SectionFormats[SectionFormats["SUBTOTAL"] = 2] = "SUBTOTAL";
    SectionFormats[SectionFormats["SIMPLE"] = 3] = "SIMPLE";
    return SectionFormats;
}({});
var BreakdownTableDepths = /*#__PURE__*/ function(BreakdownTableDepths) {
    BreakdownTableDepths[BreakdownTableDepths["PROJECT"] = -1] = "PROJECT";
    BreakdownTableDepths[BreakdownTableDepths["SECTION"] = 0] = "SECTION";
    BreakdownTableDepths[BreakdownTableDepths["MODULE"] = 1] = "MODULE";
    BreakdownTableDepths[BreakdownTableDepths["FEATURE"] = 2] = "FEATURE";
    BreakdownTableDepths[BreakdownTableDepths["TASK"] = 3] = "TASK";
    return BreakdownTableDepths;
}({});
var SummaryTableDepths = /*#__PURE__*/ function(SummaryTableDepths) {
    SummaryTableDepths[SummaryTableDepths["WORK_TYPE"] = 1] = "WORK_TYPE";
    SummaryTableDepths[SummaryTableDepths["TEAM_MEMBER"] = 2] = "TEAM_MEMBER";
    return SummaryTableDepths;
}({});
var TimelineMode = /*#__PURE__*/ function(TimelineMode) {
    TimelineMode["MIN"] = "min";
    TimelineMode["MAX"] = "max";
    return TimelineMode;
}({});
var WorkTypeValueState = /*#__PURE__*/ function(WorkTypeValueState) {
    WorkTypeValueState["SINGLE"] = "avg";
    WorkTypeValueState["MIN_MAX"] = "var";
    return WorkTypeValueState;
}({});
var TimeModifiers = /*#__PURE__*/ function(TimeModifiers) {
    TimeModifiers[TimeModifiers["DAY"] = 8] = "DAY";
    TimeModifiers[TimeModifiers["WEEK"] = 5] = "WEEK";
    TimeModifiers[TimeModifiers["MONTH"] = 20] = "MONTH";
    return TimeModifiers;
}({});
var MaxTimeModifiers = /*#__PURE__*/ function(MaxTimeModifiers) {
    MaxTimeModifiers[MaxTimeModifiers["HOUR"] = 60] = "HOUR";
    MaxTimeModifiers[MaxTimeModifiers["DAY"] = 24] = "DAY";
    MaxTimeModifiers[MaxTimeModifiers["WEEK"] = 7.75] = "WEEK";
    MaxTimeModifiers[MaxTimeModifiers["MONTH"] = 31] = "MONTH";
    return MaxTimeModifiers;
}({});

const DEFAULT_RATES = {
    2: 80,
    3: 80,
    4: 80,
    5: 100,
    6: 100,
    7: 100,
    20: 120,
    23: 80,
    21: 100
};
const DEFAULT_BREAKDOWN_COLUMNS_ORDER = [
    BreakdownColumns.NUMERATION,
    BreakdownColumns.TITLE,
    BreakdownColumns.LABELS,
    BreakdownColumns.DESCRIPTION,
    BreakdownColumns.ESTIMATES,
    BreakdownColumns.PRICE
];
const DEFAULT_BREAKDOWN_COLUMNS_WIDTHS = {
    [BreakdownColumns.TITLE]: 400,
    [BreakdownColumns.LABELS]: 120,
    [BreakdownColumns.DESCRIPTION]: 240
};
const DEFAULT_PROPOSAL_COLUMNS_ORDER = [
    SummaryColumns.TEAM,
    SummaryColumns.TIME,
    SummaryColumns.RATE,
    SummaryColumns.HOURS,
    SummaryColumns.COST
];
const DEFAULT_PROFITABILITY_COLUMNS_ORDER = [
    SummaryColumns.HOURS,
    SummaryColumns.C_UNIT_COST_INNER,
    SummaryColumns.C_UNIT_COST,
    SummaryColumns.RATE,
    SummaryColumns.C_COST,
    SummaryColumns.COST,
    SummaryColumns.C_PROFIT,
    SummaryColumns.C_PROFITABILITY
];
const DEFAULT_PROJECT_TABLES_ORDER = [
    Tables.DESCRIPTION,
    Tables.SUMMARY,
    Tables.BREAKDOWN,
    Tables.TIMELINE
];
const DEFAULT_PROJECT_TOTALS_ORDER = [
    Totals.TIME,
    Totals.HOURS,
    Totals.COST
];
const MONTH_WEEKS = 4;
const MAX_UNDO_HISTORY_SIZE = 100;
const MAX_WORK_TYPE_LENGTH = 80;
const MAX_MONTH_DELAY = 12;
const MAX_TIMELINE_LENGTH = 60;
const MAX_TIMELINE_WEEKS_LENGTH = MAX_TIMELINE_LENGTH * MONTH_WEEKS;
const LAST_TIMELINE_MONTH_INDEX = MAX_TIMELINE_LENGTH - 1;
const LAST_TIMELINE_WEEK_INDEX = MAX_TIMELINE_WEEKS_LENGTH - 1;
const MAX_TEXT_DESCRIPTION_LENGTH = 10000;
const MIN_TIMELINE_MONTH_VALUE = MaxTimeModifiers.HOUR * 0.01;
const DEFAULT_CURRENCY_KEY = "USD";
const DEFAULT_VERSION_INFO = {
    key: 1,
    order: 1,
    lock: 0,
    visibility: 1,
    name: "Version 1",
    structure: ""
};
const DEFAULT_MIN_VALUE_COEFFICIENT = 0.75;
const DEFAULT_MAX_VALUE_COEFFICIENT = 1.25;
const DEFAULT_MTA_VALUE = TimeModifiers.DAY * TimeModifiers.MONTH;
const DEFAULT_WTA_VALUE = DEFAULT_MTA_VALUE / MONTH_WEEKS;
const DEFAULT_PROFITABILITY_COLUMNS = DEFAULT_PROFITABILITY_COLUMNS_ORDER.map((name)=>({
        name,
        visible: true
    }));
const AVG_KEYS = [
    MinAvgMaxKeys.AVG
];
const MIN_MAX_KEYS = [
    MinAvgMaxKeys.MIN,
    MinAvgMaxKeys.MAX
];
const MIN_AVG_MAX_KEYS = [
    MinAvgMaxKeys.MIN,
    MinAvgMaxKeys.AVG,
    MinAvgMaxKeys.MAX
];
const MAX_DESCRIPTION_COLUMNS = 5;
const BT_MERGED_VALUE = "BT_MERGE";

const SORT_BY_TYPES = {
    NAME: "name",
    CREATED: "created",
    UPDATED: "updated"
};
const LOCAL_SORT_TYPES = {
    ASC: "asc",
    DESC: "desc"
};
const SORT_TYPES = {
    NEWEST: "newest",
    OLDEST: "oldest"
};

var CoreCommands = /*#__PURE__*/ function(CoreCommands) {
    CoreCommands["CONNECTION"] = "connection";
    CoreCommands["DISCONNECT"] = "disconnect";
    CoreCommands["ERROR"] = "error";
    CoreCommands["CONNECT_ERROR"] = "connection_error";
    CoreCommands["ACTION_ERROR"] = "action_error";
    CoreCommands["JOIN_COMPANY"] = "join_company";
    CoreCommands["JOIN_WORKSPACE"] = "join_workspace";
    CoreCommands["JOIN_WORKSPACE_PROJECTS"] = "join_workspace_projects";
    CoreCommands["LEAVE_WORKSPACE_PROJECTS"] = "leave_workspace_projects";
    CoreCommands["JOIN_PROJECT"] = "join_project";
    CoreCommands["JOIN_TEMPLATE"] = "join_template";
    CoreCommands["CLOSE_PROJECT"] = "close_project";
    CoreCommands["CLOSE_TEMPLATE"] = "close_template";
    CoreCommands["JOIN_PROPOSAL"] = "join_proposal";
    CoreCommands["CLOSE_PROPOSAL"] = "close_proposal";
    CoreCommands["PROPOSAL_SESSION_START"] = "proposalSessionStart";
    CoreCommands["WORKTYPE_ADD"] = "wt_add";
    CoreCommands["WORKTYPE_EDIT"] = "wt_change";
    CoreCommands["WORKTYPE_REMOVE"] = "wt_remove";
    CoreCommands["LABEL_REMOVE"] = "label_remove";
    CoreCommands["LABEL_UPDATE"] = "label_update";
    CoreCommands["LABEL_CREATE"] = "label_create";
    CoreCommands["NOTIFICATION"] = "notification";
    CoreCommands["ALERT"] = "alert";
    CoreCommands["LOGOUT"] = "logout";
    return CoreCommands;
}({});
var ProjectCommands = /*#__PURE__*/ function(ProjectCommands) {
    ProjectCommands["PROJECT_PROGRESS"] = "project_creation_progress";
    ProjectCommands["PROJECT_CREATE"] = "project_create";
    ProjectCommands["PROJECT_WAS_CREATED"] = "project_was_created";
    ProjectCommands["PROJECT_RENAME"] = "project_rename";
    ProjectCommands["PROJECT_STATUS"] = "project_status";
    ProjectCommands["PROJECT_REMOVE"] = "project_remove";
    ProjectCommands["PROJECT_ARCHIVE"] = "project_archive";
    ProjectCommands["PROJECT_METADATA"] = "project_metadata";
    ProjectCommands["USER_ADD"] = "user_add";
    ProjectCommands["USER_REMOVE"] = "user_remove";
    ProjectCommands["USER_PROMOTE"] = "user_promote";
    return ProjectCommands;
}({});
var TemplateCommands = /*#__PURE__*/ function(TemplateCommands) {
    TemplateCommands["TEMPLATE_PROGRESS"] = "template_open_progress";
    TemplateCommands["TEMPLATE_REMOVE"] = "template_remove";
    TemplateCommands["TEMPLATE_RENAME"] = "template_rename";
    TemplateCommands["TEMPLATE_METADATA"] = "template_metadata";
    return TemplateCommands;
}({});
var ProjectEditorCommands = /*#__PURE__*/ function(ProjectEditorCommands) {
    ProjectEditorCommands["USER_JOINED"] = "project_user_joined";
    ProjectEditorCommands["USER_LEFT"] = "project_user_left";
    ProjectEditorCommands["USER_MOVE"] = "project_user_move";
    ProjectEditorCommands["PATCH"] = "project_patch";
    ProjectEditorCommands["VERSION_REQUEST"] = "project_version_get";
    ProjectEditorCommands["VERSION_NEW"] = "project_version_add";
    ProjectEditorCommands["VERSION_IMPORT"] = "project_version_import";
    ProjectEditorCommands["VERSION_REMOVE"] = "project_version_remove";
    ProjectEditorCommands["VERSION_RENAME"] = "project_version_rename";
    ProjectEditorCommands["VERSION_ORDER"] = "project_version_order";
    ProjectEditorCommands["VERSION_VISIBILITY"] = "project_version_visibility";
    ProjectEditorCommands["VERSION_LIST"] = "project_version_list";
    ProjectEditorCommands["VERSION_LOCK"] = "project_version_locked";
    ProjectEditorCommands["PERMIT_ADD"] = "permit_add";
    ProjectEditorCommands["PERMIT_REMOVE"] = "permit_remove";
    ProjectEditorCommands["PERMIT_STATUS"] = "permit_update";
    ProjectEditorCommands["PERMIT_USER_VISIBILITY"] = "user_permit_update";
    ProjectEditorCommands["COVER_ADD"] = "cover_add";
    ProjectEditorCommands["COVER_REMOVE"] = "cover_remove";
    ProjectEditorCommands["COVER_PAGES"] = "cover_update_pages";
    ProjectEditorCommands["COVER_SCALE"] = "cover_update_scale";
    ProjectEditorCommands["SHARE_SETTINGS"] = "shareSettings";
    ProjectEditorCommands["VERSIONS_WORK_TYPE_UPDATE"] = "project_versions_wt_update";
    ProjectEditorCommands["GENERATOR_START"] = "project_generated_start";
    ProjectEditorCommands["GENERATOR_PART"] = "project_generated_part";
    ProjectEditorCommands["GENERATOR_REASONING"] = "project_generated_reasoning";
    ProjectEditorCommands["GENERATOR_END"] = "project_generator_end";
    ProjectEditorCommands["GENERATOR_STOP"] = "project_generator_stop";
    ProjectEditorCommands["LABEL_REMOVE"] = "project_label_remove";
    ProjectEditorCommands["LABEL_CREATE"] = "project_label_create";
    ProjectEditorCommands["AGENT_SESSION_START"] = "project_agent_session_start";
    ProjectEditorCommands["AGENT_SESSION_END"] = "project_agent_session_end";
    ProjectEditorCommands["AGENT_SESSION_TAKEOVER"] = "project_agent_session_takeover";
    return ProjectEditorCommands;
}({});
var ProjectCommentCommands = /*#__PURE__*/ function(ProjectCommentCommands) {
    ProjectCommentCommands["COMMENT_ADD"] = "project_comment_add";
    ProjectCommentCommands["COMMENT_EDIT"] = "project_comment_update";
    ProjectCommentCommands["COMMENT_REMOVE"] = "project_comment_remove";
    ProjectCommentCommands["COMMENT_REMOVE_PATH"] = "project_comment_path_remove";
    ProjectCommentCommands["COMMENT_MOVE"] = "project_comment_move";
    ProjectCommentCommands["COMMENT_ELEMENT_RENAME"] = "project_comment_element_rename";
    ProjectCommentCommands["COMMENT_PATCH"] = "project_comment_patch";
    return ProjectCommentCommands;
}({});
var TemplateEditorCommands = /*#__PURE__*/ function(TemplateEditorCommands) {
    TemplateEditorCommands["USER_JOINED"] = "template_user_joined";
    TemplateEditorCommands["USER_LEFT"] = "template_user_left";
    TemplateEditorCommands["USER_MOVE"] = "template_user_move";
    TemplateEditorCommands["PATCH"] = "template_patch";
    TemplateEditorCommands["VERSION_REQUEST"] = "template_version_get";
    TemplateEditorCommands["VERSION_NEW"] = "template_version_add";
    TemplateEditorCommands["VERSION_IMPORT"] = "template_version_import";
    TemplateEditorCommands["VERSION_REMOVE"] = "template_version_remove";
    TemplateEditorCommands["VERSION_RENAME"] = "template_version_rename";
    TemplateEditorCommands["VERSION_ORDER"] = "template_version_order";
    TemplateEditorCommands["VERSION_VISIBILITY"] = "template_version_visibility";
    TemplateEditorCommands["VERSION_LOCK"] = "template_version_locked";
    TemplateEditorCommands["VERSION_LIST"] = "template_version_list";
    TemplateEditorCommands["VERSIONS_WORK_TYPE_UPDATE"] = "template_versions_wt_update";
    TemplateEditorCommands["LABEL_CREATE"] = "template_label_create";
    TemplateEditorCommands["LABEL_REMOVE"] = "template_label_remove";
    return TemplateEditorCommands;
}({});
var TemplateCommentCommands = /*#__PURE__*/ function(TemplateCommentCommands) {
    TemplateCommentCommands["COMMENT_ADD"] = "template_comment_add";
    TemplateCommentCommands["COMMENT_EDIT"] = "template_comment_update";
    TemplateCommentCommands["COMMENT_REMOVE"] = "template_comment_remove";
    TemplateCommentCommands["COMMENT_REMOVE_PATH"] = "template_comment_path_remove";
    TemplateCommentCommands["COMMENT_MOVE"] = "template_comment_move";
    TemplateCommentCommands["COMMENT_ELEMENT_RENAME"] = "template_comment_element_rename";
    TemplateCommentCommands["COMMENT_PATCH"] = "template_comment_patch";
    return TemplateCommentCommands;
}({});

const roundFloat = (val, rounding, fixedString, decimals = 0)=>{
    if (!val) return val;
    const pVal = typeof val === "string" ? parseFloat(val) : val;
    if (!rounding) {
        const rounded = Math.round(pVal * 100) / 100;
        if (fixedString && rounded !== Math.round(pVal)) return rounded.toFixed(2);
        return rounded;
    }
    const decimalMod = Math.pow(10, decimals);
    switch(rounding){
        case Rounding.FLOOR:
            return Math.floor(pVal * decimalMod) / decimalMod;
        case Rounding.CEIL:
            return Math.ceil(pVal * decimalMod) / decimalMod;
        case Rounding.FULL:
            return Math.round(pVal * decimalMod) / decimalMod;
        default:
            return Math.round(pVal * 100) / 100;
    }
};

const safeRoundHours = (hours, modifier = MaxTimeModifiers.HOUR, roundHours)=>{
    if (typeof hours !== "number") return hours;
    return parseFloat((Number(roundFloat(hours / modifier, roundHours, false)) * modifier).toFixed(2));
};
const safeRoundHoursV2 = (hours, modifier = MaxTimeModifiers.HOUR, roundType, roundInterval)=>{
    if (typeof hours !== "number") return hours;
    const v = hours / modifier * roundInterval;
    let res = v;
    switch(roundType){
        case Rounding.FLOOR:
            res = Math.floor(v);
            break;
        case Rounding.CEIL:
            res = Math.ceil(v);
            break;
        case Rounding.FULL:
            res = Math.round(v);
    }
    return parseFloat((res * modifier / roundInterval).toFixed(2));
};
const safeRoundRate = (hours, modifier = MaxTimeModifiers.HOUR, roundHours)=>{
    if (typeof hours !== "number") return hours;
    return parseFloat((Number(roundFloat(hours * modifier, roundHours)) / modifier).toPrecision(12));
};

const isEmptyHtml = (testString)=>{
    // return !testString || Boolean(testString.match(/^<p><br\/?><\/?p>$/));
    return !testString || Boolean(testString.match(/^<[^<>/]+?>(<[^<>/]+?>)*(&#xFEFF;)?(<br\/?>)?(<[^<>]+?>)*<\/[^<>/]+>$/));
};
const trimEmptyRows = (string)=>{
    return string.replace(/^(<p><br\/?><\/?p>)*/g, "").replace(/(<p><br\/?><\/?p>)*$/g, "");
};
const simplifyTextString = (s)=>{
    return s?.trim()?.toLowerCase() || "";
};

const splitBySearchPhrases = (value, searchPhrases)=>{
    let splitValue = [];
    const splitIndexes = searchPhrases.map(({ value: phrase })=>{
        const start = simplifyTextString(value).indexOf(simplifyTextString(phrase));
        return [
            start,
            start + phrase.length
        ];
    }).filter((p)=>p[0] >= 0);
    let lastIndex = 0;
    for(let index = 0; index < value.length; index++){
        const phrase = splitIndexes.find(([start])=>start === index);
        if (phrase) {
            if (lastIndex < index) splitValue.push({
                value: value.slice(lastIndex, index)
            });
            splitValue.push({
                value: value.slice(phrase[0], phrase[1]),
                isSearchPhrase: true
            });
            lastIndex = phrase[1];
            index = phrase[1];
        }
    }
    if (lastIndex < value.length) splitValue.push({
        value: value.slice(lastIndex)
    });
    return splitValue;
};

const indexToRoman = (num)=>{
    const roman = {
        M: 1000,
        CM: 900,
        D: 500,
        CD: 400,
        C: 100,
        XC: 90,
        L: 50,
        XL: 40,
        X: 10,
        IX: 9,
        V: 5,
        IV: 4,
        I: 1
    };
    let str = '';
    for (const i of Object.keys(roman)){
        const q = Math.floor(num / roman[i]);
        num -= q * roman[i];
        str += i.repeat(q);
    }
    return str.toLowerCase();
};
const indexToLetter = (num)=>{
    return (num >= 26 ? indexToLetter((num / 26 >> 0) - 1) : "") + String.fromCharCode(96 + (num % 26 >> 0));
};
const formatOrder = (order, indent = 0)=>{
    if (indent % 3 === 1) return indexToLetter(order);
    if (indent % 3 === 2) return indexToRoman(order);
    return order;
};
const ulIndent = (text, indent = 0)=>unescape(`${"    ".repeat(Number(indent) + 1)}• ${text}`);
const olIndent = (text, order, indent = 0)=>`${"    ".repeat(Number(indent) + 1)}${formatOrder(order, indent)}. ${text}`;

const mapQuillDeltaToRichText = (delta)=>{
    let numeration = [];
    let numeratorPause = null;
    let lastIndent = -1;
    let plainText = "";
    const richText = delta?.ops?.reduce((list, { insert, attributes })=>{
        if (!insert) return list;
        const font = {
            size: 10,
            scheme: "minor"
        };
        let text = insert;
        plainText += insert;
        if (attributes?.list) {
            const indent = attributes.indent || 0;
            if (numeration[indent]) numeration[indent]++;
            else numeration[indent] = 1;
            const order = numeration[indent];
            const formatList = (text)=>(attributes.list === "ordered" ? olIndent(text, order, indent) : ulIndent(text, indent)) + "\n";
            const last = list[list.length - 1];
            const index = last.text.lastIndexOf("\n");
            if (index > 0) {
                const lastText = last.text.slice(index + 1);
                last.text = last.text.slice(0, index + 1);
                list.push({
                    font,
                    text: formatList(lastText)
                });
            } else {
                last.text = formatList(last.text);
            }
            if (lastIndent > indent) for(let i = lastIndent; i > indent; --i)delete numeration[i];
            lastIndent = indent;
            numeratorPause = 1;
            return list;
        } else if (numeratorPause) numeratorPause++;
        if (numeratorPause === 3) numeration = [];
        if (attributes) Object.keys(attributes).forEach((key)=>{
            switch(key){
                case "code":
                    text = "  " + text;
                    font.size = 10;
                    font.name = "Consolas";
                    font.color = {
                        argb: "#FF999999"
                    };
                    break;
                case "blockquote":
                    text = " |  " + text;
                    break;
                case "header":
                    font.size = -(attributes[key] - 1) * 4 + 20;
                    break;
                default:
                    // @ts-ignore
                    font[key] = attributes[key];
            }
        });
        list.push({
            text,
            font
        });
        return list;
    }, []) || [];
    return {
        text: plainText,
        richText
    };
};

/**
 * Performs a deep merge of objects and returns new object. Does not modify
 * objects (immutable) and merges arrays via concatenation.
 *
 * @param {...object} objects - Objects to merge
 * @returns {object} New object with merged key/values
 */ function mergeDeep(...objects) {
    const isObject = (obj)=>obj && typeof obj === "object";
    return objects.reduce((prev, obj)=>{
        Object.keys(obj).forEach((key)=>{
            const pVal = prev[key];
            const oVal = obj[key];
            if (Array.isArray(pVal) && Array.isArray(oVal)) {
                prev[key] = pVal.concat(...oVal);
            } else if (isObject(pVal) && isObject(oVal)) {
                prev[key] = mergeDeep(pVal, oVal);
            } else {
                prev[key] = oVal;
            }
        });
        return prev;
    }, {});
}

const defaultTextBlock = (text)=>({
        font: {
            size: 10,
            scheme: "minor"
        },
        text
    });
const defaultEndLineBlock = ()=>({
        text: "\n"
    });
const listLineBlock = {
    tag: /<\/?li data-list="(\w+)"( class="ql-indent-(\d+)")?>(<span class="ql-ui" contenteditable="false"><\/span>)?/,
    block: (text, order, bullet, indent)=>({
            text: bullet ? ulIndent(text, indent) : order && order > 0 ? olIndent(text, order, indent) : `    ${text}`
        })
};
const hyperlinkBlock = {
    tag: /<\/?a.*?>/,
    block: (text)=>({
            font: {
                size: 10,
                scheme: "minor",
                color: {
                    argb: "#FF1155CC"
                }
            },
            text
        }),
    checkHref: true
};
const rdiMap = [
    {
        tag: /<\/?p>/,
        block: defaultTextBlock
    },
    {
        tag: /<\/?blockquote>/,
        block: (text)=>({
                font: {
                    size: 11,
                    scheme: "minor"
                },
                text: ` |  ${text}`
            })
    },
    {
        tag: /<\/?pre>/,
        block: (text)=>({
                font: {
                    size: 10,
                    scheme: "minor",
                    name: "Consolas",
                    color: {
                        argb: "#FF999999"
                    }
                },
                text: `  ${text}`
            })
    },
    hyperlinkBlock,
    {
        tag: /<\/?h1>/,
        block: (text)=>({
                font: {
                    size: 20,
                    scheme: "minor"
                },
                text
            })
    },
    {
        tag: /<\/?h2>/,
        block: (text)=>({
                font: {
                    size: 16,
                    scheme: "minor"
                },
                text
            })
    },
    {
        tag: /<\/?h3>/,
        block: (text)=>({
                font: {
                    size: 12,
                    scheme: "minor"
                },
                text
            })
    },
    {
        tag: /<\/?ol>/,
        block: defaultEndLineBlock,
        initOrder: true
    },
    {
        tag: /<\/?ul>/,
        block: defaultEndLineBlock,
        useBullet: true
    },
    listLineBlock,
    //inner block modifiers
    {
        tag: /<\/?code>/,
        block: (text)=>({
                font: {
                    size: 10,
                    scheme: "minor",
                    color: {
                        argb: "#FF999999"
                    }
                },
                text
            })
    },
    {
        tag: /<\/?strong>/,
        block: (text)=>({
                font: {
                    bold: true
                },
                text
            })
    },
    {
        tag: /<\/?em>/,
        block: (text)=>({
                font: {
                    italic: true
                },
                text
            })
    },
    {
        tag: /<\/?s>/,
        block: (text)=>({
                font: {
                    strike: true
                },
                text
            })
    },
    {
        tag: /<\/?u>/,
        block: (text)=>({
                font: {
                    underline: "single"
                },
                text
            })
    }
];
const mapHtmlToRichText = (htmlString)=>{
    let richText = [];
    if (!htmlString.match(/<\/?\w+ ?.*?>/g)) return htmlString;
    htmlString = htmlString.replace(/<\/?span ?(style=".+?")?>/g, "");
    const text = htmlString.replace(/<\/p>/g, " ").replace(/<\/?\w+ ?.*?>/g, "");
    let lastIndent = -1;
    let numeration = [];
    const parseLine = (innerHtmlString, parentBlock, order, useBullet, indent)=>{
        let orderOrBulletApplied = false;
        if (innerHtmlString === "<br>") {
            richText.push(parentBlock.block("\n", order, useBullet, indent));
            return;
        }
        if (!innerHtmlString.match(/<\/?\w+ ?.*?>/g)) {
            richText.push(parentBlock.block(innerHtmlString, order, useBullet, indent));
            richText.push(defaultEndLineBlock());
            return;
        }
        let enteredBlock = [
            parentBlock
        ];
        const checkAndApplyOrderOrBullet = (text)=>{
            if (orderOrBulletApplied || !(order || useBullet)) return false;
            orderOrBulletApplied = true;
            richText.push(listLineBlock.block(text, order, useBullet, indent));
            // enteredBlock[enteredBlock.length-1] = { block: defaultTextBlock };
            enteredBlock = [];
            return true;
        };
        innerHtmlString.split(/(<\/?\w+>)/).forEach((part)=>{
            if (part === "") return;
            if (part.match(/(<\/?\w+>)/)) {
                checkAndApplyOrderOrBullet("");
                const matchedBlock = rdiMap.find((el)=>part.match(el.tag));
                if (part.match(/^<\//)) enteredBlock.pop();
                else if (matchedBlock) enteredBlock.push(matchedBlock);
                return;
            }
            const hyperlinkTag = part.match(hyperlinkBlock.tag)?.[0];
            if (hyperlinkTag) {
                const href = hyperlinkTag.match(/href="(.*?)"/)?.[1];
                if (href) part = href;
                enteredBlock.push(hyperlinkBlock);
            }
            const applied = checkAndApplyOrderOrBullet(part);
            if (!applied) {
                if (!enteredBlock.length) richText.push(defaultTextBlock(part));
                else {
                    richText.push(mergeDeep(...enteredBlock.slice().reverse().map((el)=>el.block?.(part) || defaultTextBlock(part))));
                }
            // richText.push(enteredBlock[enteredBlock.length-1].block(part))
            }
        });
        richText.push(defaultEndLineBlock());
    };
    htmlString.split(/<\/(?:pre|p|h.|ol|ul|blockquote)>/).slice(0, -1).forEach((part)=>{
        if (part.match(/^<pre/)) {
            part = part.replace(` class="ql-syntax"`, "").replace(/ spellcheck=".+"/, "").replace(/\n/g, "\n  ");
        }
        const tag = part.match(/^(<\w+>)/)?.[0];
        if (!tag) return;
        const innerHtml = part.replace(tag, "");
        const matchedBlock = rdiMap.find((el)=>tag.match(el.tag));
        if (!matchedBlock) return;
        if (matchedBlock.initOrder || matchedBlock.useBullet) {
            innerHtml.split(/<\/li>/).slice(0, -1).forEach((line)=>{
                const match = line.match(listLineBlock.tag);
                const indent = Number(match?.[3] || 0);
                if (lastIndent > indent) for(let i = lastIndent; i > indent; --i)delete numeration[i];
                lastIndent = indent;
                if (numeration[indent]) numeration[indent]++;
                else numeration[indent] = 1;
                parseLine(match?.[0] ? line.replace(match[0], "") : line, listLineBlock, numeration[indent], match?.[1] === "bullet", indent);
            });
            lastIndent = -1;
            numeration = [];
            return;
        }
        parseLine(innerHtml, matchedBlock);
    });
    if (JSON.stringify(richText[richText.length - 1]) === JSON.stringify(defaultEndLineBlock())) richText.pop();
    return {
        text,
        richText
    };
};

const urlRegex = /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&\/=]*)/;
const mapRichTextToHtml = (richText)=>{
    let htmlString = "";
    let openedLine = [];
    let justHadNewLine = false;
    const openBlock = (tag)=>{
        if (openedLine[openedLine.length - 1] === tag) return;
        openedLine.push(tag);
        htmlString += `<${tag}>`;
    };
    const closeBlock = ()=>{
        if (openedLine.length) {
            const tag = openedLine.pop();
            if (tag) htmlString += `</${tag.replace(/ class="ql-indent-\d+"/, "")}>`;
        }
    };
    const handleListBlock = (text, tag)=>{
        if (!openedLine.includes(tag)) openBlock(tag);
        if (openedLine.find((tag)=>tag.match(/^li ?.*$/))) closeBlock();
        const match = text.match(/^( *)(\d+\.|-|•) /);
        const indent = match ? Math.floor(match[1].length / 4) : 1;
        openBlock(indent > 1 ? `li class="ql-indent-${indent - 1}"` : "li");
        if (match) htmlString += text.replace(match[0], "");
    };
    richText = richText.reduce((list, line)=>{
        if (line.text.includes("\n\n")) {
            const lines = line.text.split("\n\n");
            lines.forEach((subLine, index)=>{
                if (index > 0) list.push({
                    text: "\n"
                }, {
                    text: "\n"
                });
                list.push({
                    ...line,
                    text: subLine + "\n"
                });
            });
        } else list.push(line);
        return list;
    }, []);
    richText.forEach((line)=>{
        if (line.text === "\n" || line.text === "") {
            closeBlock();
            if (justHadNewLine) {
                htmlString += "<p><br></p>";
            }
            justHadNewLine = true;
            return;
        }
        justHadNewLine = false;
        if (line.text.length > 1 && line.text.includes("\n")) {
            htmlString += mapRichTextToHtml(line.text.split("\n").map((t)=>({
                    text: t
                })));
            return;
        }
        if (line.text.match(/^( {1,4})*\d+\. /)) {
            handleListBlock(line.text, "ol");
            return;
        }
        if (line.text.match(/^( {1,4})*([-•]) /)) {
            handleListBlock(line.text, "ul");
            return;
        }
        const isH1 = line.font?.size === 20, isH2 = line.font?.size === 16, isH3 = line.font?.size === 12, isBlockquote = line.font?.size === 11 && line.text.match(/^ | {2}/), isPre = line.text.match(/^ {2}/) && line.font?.name === "Consolas", isHyperLink = Boolean(line.font?.color?.argb === "#FF65AAF6" || line.text.match(urlRegex));
        let text = line.text;
        if (!openedLine.length) {
            if (isH1) openBlock("h1");
            else if (isH2) openBlock("h2");
            else if (isH3) openBlock("h3");
            else if (isBlockquote) {
                openBlock("blockquote");
                text = text.replace(" |  ", "");
            } else if (isPre) {
                openBlock("pre");
                text = text.replace("  ", "").replace(/\n {2}/g, "\n");
            } else openBlock("p");
        }
        if (line.font?.bold) text = `<strong>${text}</strong>`;
        if (line.font?.italic) text = `<em>${text}</em>`;
        if (line.font?.strike) text = `<s>${text}</s>`;
        if (line.font?.underline) text = `<u>${text}</u>`;
        if (isHyperLink) text = `<a href="${text}">${text}</a>`;
        // if(isH1)
        //   text = `<h1>${text}</h1>`
        // else if(isH2)
        //   text = `<h2>${text}</h2>`
        // else if(isH3)
        //   text = `<h3>${text}</h3>`
        // else if(isBlockquote)
        //   text = `<blockquote>${text}</blockquote>`
        htmlString += text;
    });
    while(openedLine.length)closeBlock();
    return htmlString;
};

const parseCsv = (v)=>(v || "").split(",").map((s)=>s.trim()).filter((s)=>s.length > 0);
const parseNonNegativeInt = (v)=>{
    if (!v) return undefined;
    const n = Number(v);
    if (!Number.isFinite(n)) return undefined;
    const i = Math.floor(n);
    return i >= 0 ? i : undefined;
};
const parseDomainList = (v)=>{
    if (!v) return [];
    let domainList = [];
    const raw = v.trim();
    if (raw.startsWith("[") && raw.endsWith("]")) {
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                domainList = parsed.map((x)=>String(x).trim()).filter(Boolean);
            }
        } catch  {
            domainList = parseCsv(v);
        }
    } else {
        domainList = parseCsv(v);
    }
    return domainList?.map((domain)=>{
        if (!domain.match(/^https?:\/\//)) return "https://" + domain;
        return domain;
    });
};

const currencies = [
    {
        symbolStart: "$",
        name: "US Dollar",
        symbolNative: "$",
        code: "USD",
        order: 1
    },
    {
        symbolStart: "€",
        name: "Euro",
        symbolNative: "€",
        code: "EUR",
        order: 2
    },
    {
        symbolStart: "£",
        name: "British Pound Sterling",
        symbolNative: "£",
        code: "GBP",
        order: 3
    },
    {
        symbolStart: "$",
        name: "Australian Dollar",
        symbolNative: "$",
        code: "AUD",
        order: 4
    },
    {
        symbolStart: "$",
        name: "Canadian Dollar",
        symbolNative: "$",
        code: "CAD",
        order: 5
    },
    {
        symbolEnd: "zł",
        name: "Polish Zloty",
        symbolNative: "zł",
        code: "PLN",
        order: 6
    },
    {
        symbolEnd: "Rs",
        name: "Indian Rupee",
        symbolNative: "টকা",
        code: "INR",
        order: 7
    },
    {
        symbolEnd: "kr",
        name: "Norwegian Krone",
        symbolNative: "kr",
        code: "NOK",
        order: 8
    },
    {
        symbolEnd: "kr",
        name: "Swedish Krona",
        symbolNative: "kr",
        code: "SEK",
        order: 9
    },
    {
        symbolEnd: "kr",
        name: "Danish Krone",
        symbolNative: "kr",
        code: "DKK",
        order: 10
    },
    {
        symbolEnd: "AED",
        name: "United Arab Emirates Dirham",
        symbolNative: "د.إ.‏",
        code: "AED"
    },
    {
        symbolEnd: "Af",
        name: "Afghan Afghani",
        symbolNative: "؋",
        code: "AFN"
    },
    {
        symbolEnd: "ALL",
        name: "Albanian Lek",
        symbolNative: "Lek",
        code: "ALL"
    },
    {
        symbolEnd: "AMD",
        name: "Armenian Dram",
        symbolNative: "դր.",
        code: "AMD"
    },
    {
        symbolEnd: "$",
        name: "Argentine Peso",
        symbolNative: "$",
        code: "ARS"
    },
    {
        symbolEnd: "man.",
        name: "Azerbaijani Manat",
        symbolNative: "ман.",
        code: "AZN"
    },
    {
        symbolEnd: "KM",
        name: "Bosnia-Herzegovina Convertible Mark",
        symbolNative: "KM",
        code: "BAM"
    },
    {
        symbolEnd: "Tk",
        name: "Bangladeshi Taka",
        symbolNative: "৳",
        code: "BDT"
    },
    {
        symbolEnd: "лв",
        name: "Bulgarian Lev",
        symbolNative: "лв.",
        code: "BGN"
    },
    {
        symbolEnd: "BD",
        name: "Bahraini Dinar",
        symbolNative: "د.ب.‏",
        code: "BHD"
    },
    {
        symbolEnd: "FBu",
        name: "Burundian Franc",
        symbolNative: "FBu",
        code: "BIF"
    },
    {
        symbolStart: "$",
        name: "Brunei Dollar",
        symbolNative: "$",
        code: "BND"
    },
    {
        symbolEnd: "Bs",
        name: "Bolivian Boliviano",
        symbolNative: "Bs",
        code: "BOB"
    },
    {
        symbolEnd: "R$",
        name: "Brazilian Real",
        symbolNative: "R$",
        code: "BRL"
    },
    {
        symbolEnd: "BWP",
        name: "Botswanan Pula",
        symbolNative: "P",
        code: "BWP"
    },
    {
        symbolEnd: "Br",
        name: "Belarusian Ruble",
        symbolNative: "руб",
        code: "BYN"
    },
    {
        symbolStart: "$",
        name: "Belize Dollar",
        symbolNative: "$",
        code: "BZD"
    },
    {
        symbolEnd: "CDF",
        name: "Congolese Franc",
        symbolNative: "FrCD",
        code: "CDF"
    },
    {
        symbolEnd: "CHF",
        name: "Swiss Franc",
        symbolNative: "CHF",
        code: "CHF"
    },
    {
        symbolEnd: "$",
        name: "Chilean Peso",
        symbolNative: "$",
        code: "CLP"
    },
    {
        symbolEnd: "CN¥",
        name: "Chinese Yuan",
        symbolNative: "CN¥",
        code: "CNY"
    },
    {
        symbolEnd: "$",
        name: "Colombian Peso",
        symbolNative: "$",
        code: "COP"
    },
    {
        symbolEnd: "₡",
        name: "Costa Rican Colón",
        symbolNative: "₡",
        code: "CRC"
    },
    {
        symbolEnd: "$",
        name: "Cape Verdean Escudo",
        symbolNative: "CV$",
        code: "CVE"
    },
    {
        symbolEnd: "Kč",
        name: "Czech Republic Koruna",
        symbolNative: "Kč",
        code: "CZK"
    },
    {
        symbolEnd: "Fdj",
        name: "Djiboutian Franc",
        symbolNative: "Fdj",
        code: "DJF"
    },
    {
        symbolEnd: "$",
        name: "Dominican Peso",
        symbolNative: "RD$",
        code: "DOP"
    },
    {
        symbolEnd: "DA",
        name: "Algerian Dinar",
        symbolNative: "د.ج.‏",
        code: "DZD"
    },
    {
        symbolEnd: "kr",
        name: "Estonian Kroon",
        symbolNative: "kr",
        code: "EEK"
    },
    {
        symbolEnd: "EGP",
        name: "Egyptian Pound",
        symbolNative: "ج.م.‏",
        code: "EGP"
    },
    {
        symbolEnd: "Nfk",
        name: "Eritrean Nakfa",
        symbolNative: "Nfk",
        code: "ERN"
    },
    {
        symbolEnd: "Br",
        name: "Ethiopian Birr",
        symbolNative: "Br",
        code: "ETB"
    },
    {
        symbolEnd: "GEL",
        name: "Georgian Lari",
        symbolNative: "GEL",
        code: "GEL"
    },
    {
        symbolEnd: "GH₵",
        name: "Ghanaian Cedi",
        symbolNative: "GH₵",
        code: "GHS"
    },
    {
        symbolEnd: "FG",
        name: "Guinean Franc",
        symbolNative: "FG",
        code: "GNF"
    },
    {
        symbolEnd: "GTQ",
        name: "Guatemalan Quetzal",
        symbolNative: "Q",
        code: "GTQ"
    },
    {
        symbolStart: "$",
        name: "Hong Kong Dollar",
        symbolNative: "$",
        code: "HKD"
    },
    {
        symbolEnd: "HNL",
        name: "Honduran Lempira",
        symbolNative: "L",
        code: "HNL"
    },
    {
        symbolEnd: "kn",
        name: "Croatian Kuna",
        symbolNative: "kn",
        code: "HRK"
    },
    {
        symbolEnd: "Ft",
        name: "Hungarian Forint",
        symbolNative: "Ft",
        code: "HUF"
    },
    {
        symbolEnd: "Rp",
        name: "Indonesian Rupiah",
        symbolNative: "Rp",
        code: "IDR"
    },
    {
        symbolEnd: "₪",
        name: "Israeli New Sheqel",
        symbolNative: "₪",
        code: "ILS"
    },
    {
        symbolEnd: "IQD",
        name: "Iraqi Dinar",
        symbolNative: "د.ع.‏",
        code: "IQD"
    },
    {
        symbolEnd: "IRR",
        name: "Iranian Rial",
        symbolNative: "﷼",
        code: "IRR"
    },
    {
        symbolEnd: "kr",
        name: "Icelandic Króna",
        symbolNative: "kr",
        code: "ISK"
    },
    {
        symbolStart: "$",
        name: "Jamaican Dollar",
        symbolNative: "$",
        code: "JMD"
    },
    {
        symbolEnd: "JD",
        name: "Jordanian Dinar",
        symbolNative: "د.أ.‏",
        code: "JOD"
    },
    {
        symbolEnd: "¥",
        name: "Japanese Yen",
        symbolNative: "￥",
        code: "JPY"
    },
    {
        symbolEnd: "Ksh",
        name: "Kenyan Shilling",
        symbolNative: "Ksh",
        code: "KES"
    },
    {
        symbolEnd: "KHR",
        name: "Cambodian Riel",
        symbolNative: "៛",
        code: "KHR"
    },
    {
        symbolEnd: "CF",
        name: "Comorian Franc",
        symbolNative: "FC",
        code: "KMF"
    },
    {
        symbolEnd: "₩",
        name: "South Korean Won",
        symbolNative: "₩",
        code: "KRW"
    },
    {
        symbolEnd: "KD",
        name: "Kuwaiti Dinar",
        symbolNative: "د.ك.‏",
        code: "KWD"
    },
    {
        symbolEnd: "KZT",
        name: "Kazakhstani Tenge",
        symbolNative: "тңг.",
        code: "KZT"
    },
    {
        symbolEnd: "LB£",
        name: "Lebanese Pound",
        symbolNative: "ل.ل.‏",
        code: "LBP"
    },
    {
        symbolEnd: "SLRs",
        name: "Sri Lankan Rupee",
        symbolNative: "SL Re",
        code: "LKR"
    },
    {
        symbolEnd: "Lt",
        name: "Lithuanian Litas",
        symbolNative: "Lt",
        code: "LTL"
    },
    {
        symbolEnd: "Ls",
        name: "Latvian Lats",
        symbolNative: "Ls",
        code: "LVL"
    },
    {
        symbolEnd: "LD",
        name: "Libyan Dinar",
        symbolNative: "د.ل.‏",
        code: "LYD"
    },
    {
        symbolEnd: "MAD",
        name: "Moroccan Dirham",
        symbolNative: "د.م.‏",
        code: "MAD"
    },
    {
        symbolEnd: "lei",
        name: "Moldovan Leu",
        symbolNative: "MDL",
        code: "MDL"
    },
    {
        symbolEnd: "MGA",
        name: "Malagasy Ariary",
        symbolNative: "MGA",
        code: "MGA"
    },
    {
        symbolEnd: "MKD",
        name: "Macedonian Denar",
        symbolNative: "MKD",
        code: "MKD"
    },
    {
        symbolEnd: "MMK",
        name: "Myanma Kyat",
        symbolNative: "K",
        code: "MMK"
    },
    {
        symbolEnd: "$",
        name: "Macanese Pataca",
        symbolNative: "MOP$",
        code: "MOP"
    },
    {
        symbolEnd: "MURs",
        name: "Mauritian Rupee",
        symbolNative: "MURs",
        code: "MUR"
    },
    {
        symbolEnd: "$",
        name: "Mexican Peso",
        symbolNative: "$",
        code: "MXN"
    },
    {
        symbolEnd: "RM",
        name: "Malaysian Ringgit",
        symbolNative: "RM",
        code: "MYR"
    },
    {
        symbolEnd: "MTn",
        name: "Mozambican Metical",
        symbolNative: "MTn",
        code: "MZN"
    },
    {
        symbolStart: "$",
        name: "Namibian Dollar",
        symbolNative: "N$",
        code: "NAD"
    },
    {
        symbolEnd: "₦",
        name: "Nigerian Naira",
        symbolNative: "₦",
        code: "NGN"
    },
    {
        symbolEnd: "$",
        name: "Nicaraguan Córdoba",
        symbolNative: "C$",
        code: "NIO"
    },
    {
        symbolEnd: "NPRs",
        name: "Nepalese Rupee",
        symbolNative: "नेरू",
        code: "NPR"
    },
    {
        symbolStart: "$",
        name: "New Zealand Dollar",
        symbolNative: "$",
        code: "NZD"
    },
    {
        symbolEnd: "OMR",
        name: "Omani Rial",
        symbolNative: "ر.ع.‏",
        code: "OMR"
    },
    {
        symbolEnd: "B/.",
        name: "Panamanian Balboa",
        symbolNative: "B/.",
        code: "PAB"
    },
    {
        symbolEnd: "S/.",
        name: "Peruvian Nuevo Sol",
        symbolNative: "S/.",
        code: "PEN"
    },
    {
        symbolEnd: "₱",
        name: "Philippine Peso",
        symbolNative: "₱",
        code: "PHP"
    },
    {
        symbolEnd: "PKRs",
        name: "Pakistani Rupee",
        symbolNative: "₨",
        code: "PKR"
    },
    {
        symbolEnd: "₲",
        name: "Paraguayan Guarani",
        symbolNative: "₲",
        code: "PYG"
    },
    {
        symbolEnd: "QR",
        name: "Qatari Rial",
        symbolNative: "ر.ق.‏",
        code: "QAR"
    },
    {
        symbolEnd: "lei",
        name: "Romanian Leu",
        symbolNative: "RON",
        code: "RON"
    },
    {
        symbolEnd: "din.",
        name: "Serbian Dinar",
        symbolNative: "дин.",
        code: "RSD"
    },
    {
        symbolEnd: "RUB",
        name: "Russian Ruble",
        symbolNative: "₽.",
        code: "RUB"
    },
    {
        symbolEnd: "RWF",
        name: "Rwandan Franc",
        symbolNative: "FR",
        code: "RWF"
    },
    {
        symbolEnd: "SR",
        name: "Saudi Riyal",
        symbolNative: "ر.س.‏",
        code: "SAR"
    },
    {
        symbolEnd: "SDG",
        name: "Sudanese Pound",
        symbolNative: "SDG",
        code: "SDG"
    },
    {
        symbolStart: "$",
        name: "Singapore Dollar",
        symbolNative: "$",
        code: "SGD"
    },
    {
        symbolEnd: "Ssh",
        name: "Somali Shilling",
        symbolNative: "Ssh",
        code: "SOS"
    },
    {
        symbolEnd: "SY£",
        name: "Syrian Pound",
        symbolNative: "ل.س.‏",
        code: "SYP"
    },
    {
        symbolEnd: "฿",
        name: "Thai Baht",
        symbolNative: "฿",
        code: "THB"
    },
    {
        symbolEnd: "DT",
        name: "Tunisian Dinar",
        symbolNative: "د.ت.‏",
        code: "TND"
    },
    {
        symbolEnd: "$",
        name: "Tongan Paʻanga",
        symbolNative: "T$",
        code: "TOP"
    },
    {
        symbolEnd: "TL",
        name: "Turkish Lira",
        symbolNative: "TL",
        code: "TRY"
    },
    {
        symbolStart: "$",
        name: "Trinidad and Tobago Dollar",
        symbolNative: "$",
        code: "TTD"
    },
    {
        symbolStart: "$",
        name: "New Taiwan Dollar",
        symbolNative: "NT$",
        code: "TWD"
    },
    {
        symbolEnd: "TSh",
        name: "Tanzanian Shilling",
        symbolNative: "TSh",
        code: "TZS"
    },
    {
        symbolEnd: "₴",
        name: "Ukrainian Hryvnia",
        symbolNative: "₴",
        code: "UAH"
    },
    {
        symbolEnd: "USh",
        name: "Ugandan Shilling",
        symbolNative: "USh",
        code: "UGX"
    },
    {
        symbolEnd: "$",
        name: "Uruguayan Peso",
        symbolNative: "$",
        code: "UYU"
    },
    {
        symbolEnd: "UZS",
        name: "Uzbekistan Som",
        symbolNative: "UZS",
        code: "UZS"
    },
    {
        symbolEnd: "Bs.F.",
        name: "Venezuelan Bolívar",
        symbolNative: "Bs.F.",
        code: "VEF"
    },
    {
        symbolEnd: "₫",
        name: "Vietnamese Dong",
        symbolNative: "₫",
        code: "VND"
    },
    {
        symbolEnd: "FCFA",
        name: "CFA Franc BEAC",
        symbolNative: "FCFA",
        code: "XAF"
    },
    {
        symbolEnd: "CFA",
        name: "CFA Franc BCEAO",
        symbolNative: "CFA",
        code: "XOF"
    },
    {
        symbolEnd: "YR",
        name: "Yemeni Rial",
        symbolNative: "ر.ي.‏",
        code: "YER"
    },
    {
        symbolEnd: "R",
        name: "South African Rand",
        symbolNative: "R",
        code: "ZAR"
    },
    {
        symbolEnd: "ZK",
        name: "Zambian Kwacha",
        symbolNative: "ZK",
        code: "ZMK"
    },
    {
        symbolStart: "$",
        name: "Zimbabwean Dollar",
        symbolNative: "ZWL$",
        code: "ZWL"
    }
];
const getCurrencies = ()=>currencies;
const getCurrency = (code)=>currencies.find((c)=>c.code === code);
const matchStringToPrice = currencies.map((currency)=>{
    if (currency.symbolStart) return (v)=>Boolean(v.match("^" + currency.symbolStart?.replace(/\$/, "\\$")));
    return (v)=>Boolean(v.match(currency.symbolEnd?.replace(/\$/, "\\$") + "$"));
});

const getExchangeRate = (rates, fromCurrency, toCurrency)=>{
    const fromCurrencyRate = rates.find((r)=>r.code === fromCurrency)?.value || 1;
    const toCurrencyRate = rates.find((r)=>r.code === toCurrency)?.value || 1;
    return fromCurrencyRate * (1 / toCurrencyRate);
};

const hashCode = (code)=>{
    return code.toString().split("").reduce((s, c)=>Math.imul(31, s) + c.charCodeAt(0) | 0, 0);
};

const zip = (JSONString)=>{
    return Buffer.from(deflateRaw(JSONString, {
        level: 9
    })).toString("base64").replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
};
const unzip = (zippedString)=>{
    const prepared = zippedString.replace(/-/g, "+").replace(/_/g, "/");
    return inflateRaw(Buffer.from(prepared, "base64"), {
        toText: true
    });
};

export { APIMethod, AVG_KEYS, BT_MERGED_VALUE, BreakdownColumns, BreakdownTableDepths, CommentTypes, CoreCommands, DEFAULT_BREAKDOWN_COLUMNS_ORDER, DEFAULT_BREAKDOWN_COLUMNS_WIDTHS, DEFAULT_CURRENCY_KEY, DEFAULT_MAX_VALUE_COEFFICIENT, DEFAULT_MIN_VALUE_COEFFICIENT, DEFAULT_MTA_VALUE, DEFAULT_PROFITABILITY_COLUMNS, DEFAULT_PROFITABILITY_COLUMNS_ORDER, DEFAULT_PROJECT_TABLES_ORDER, DEFAULT_PROJECT_TOTALS_ORDER, DEFAULT_PROPOSAL_COLUMNS_ORDER, DEFAULT_RATES, DEFAULT_VERSION_INFO, DEFAULT_WTA_VALUE, DescriptionModes, EstimationUnits, LAST_TIMELINE_MONTH_INDEX, LAST_TIMELINE_WEEK_INDEX, LOCAL_SORT_TYPES, MAX_DESCRIPTION_COLUMNS, MAX_MONTH_DELAY, MAX_TEXT_DESCRIPTION_LENGTH, MAX_TIMELINE_LENGTH, MAX_TIMELINE_WEEKS_LENGTH, MAX_UNDO_HISTORY_SIZE, MAX_WORK_TYPE_LENGTH, MIN_AVG_MAX_KEYS, MIN_MAX_KEYS, MIN_TIMELINE_MONTH_VALUE, MONTH_WEEKS, MaxTimeModifiers, MinAvgMaxKeys, PriceFormats, PriceModifierModes, PriceModifiers, ProjectCommands, ProjectCommentCommands, ProjectEditorCommands, RoundIntervals, RoundTypes, Rounding, SORT_BY_TYPES, SORT_TYPES, SectionFormats, SummaryColumns, SummaryTableDepths, Tables, TaskTypes, TemplateCommands, TemplateCommentCommands, TemplateEditorCommands, TimeModifiers, TimelineMode, Totals, WorkTypeValueState, cleanHtmlContent, cleanObject, getContrastingFontColor, getCurrencies, getCurrency, getExchangeRate, hashCode, isEmptyHtml, mapHtmlToRichText, mapKeysToCamelCase, mapKeysToSnakeCase, mapQuillDeltaToRichText, mapRichTextToHtml, matchStringToPrice, mergeDeep, parseCompanySettings, parseCsv, parseDomainList, parseFormData, parseNonNegativeInt, parsePrice, roundFloat, safeRoundHours, safeRoundHoursV2, safeRoundRate, simplifyTextString, splitBySearchPhrases, trimEmptyRows, unzip, urlRegex, zip };
